import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import { I18nProvider } from "./lib/i18n.jsx";
import Layout from "./components/Layout.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import PublicSurveyPage from "./pages/PublicSurveyPage.jsx";
import ForceChangePasswordPage from "./pages/ForceChangePasswordPage.jsx";
import PublicQuoteApprovalPage from "./pages/PublicQuoteApprovalPage.jsx";
import { PROTECTED_ROUTES } from "./config/routes.js";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="h-screen flex items-center justify-center text-sm text-[#6C757D]">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Layout>{children}</Layout>;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (!["admin", "national_aftersales_manager"].includes(user?.role)) {
    return <Navigate to="/anuncios" replace />;
  }
  return children;
}

function ModuleGuard({ module, children }) {
  const { user } = useAuth();
  // When effective_modules is not present (older sessions), fall back to allow.
  const modules = user?.effective_modules;
  const allowed = !module || !modules || modules.includes(module);
  React.useEffect(() => {
    if (!allowed) {
      toast.error(`Não tem acesso ao módulo "${module}". Peça ao administrador.`, {
        duration: 4000,
      });
    }
  }, [allowed, module]);
  if (!allowed) {
    // Pick a safe landing page the user can actually access.
    // Order of preference: dashboard → first allowed module route → profile.
    const prefs = [
      ["dashboard", "/"],
      ["service_cases", "/service-cases"],
      ["tasks", "/tasks"],
      ["crm", "/customers"],
      ["parts_requests", "/parts-requests"],
      ["warranty_claims", "/warranty-claims"],
      ["body_shop_cases", "/body-shop-cases"],
      ["vehicle_registrations", "/vehicle-registrations"],
      ["reports", "/reports"],
    ];
    const fallback = prefs.find(([m]) => modules?.includes(m));
    return <Navigate to={fallback ? fallback[1] : "/profile"} replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-right" />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/surveys/:token" element={<PublicSurveyPage />} />
            <Route path="/change-password" element={<ForceChangePasswordPage />} />
            <Route path="/aprovar-orcamento/:token" element={<PublicQuoteApprovalPage />} />

            {/* Protected routes — centralised in config/routes.js */}
            {PROTECTED_ROUTES.map(({ path, Component, admin, module }) => {
              let inner = <Component />;
              if (module) inner = <ModuleGuard module={module}>{inner}</ModuleGuard>;
              if (admin) inner = <AdminGuard>{inner}</AdminGuard>;
              return (
                <Route
                  key={path}
                  path={path}
                  element={<Protected>{inner}</Protected>}
                />
              );
            })}

            <Route path="*" element={<Navigate to="/anuncios" replace />} />
          </Routes>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
